import { useCallback, useEffect, useState, type ReactNode, type RefObject } from 'react';
import { Smartphone, Trash2 } from 'lucide-react';
import { Button, Dialog } from '@paige/ui';
import {
  enrolledFactors,
  removeFactor,
  secondFactorAvailable,
  secondStepMessage,
  type EnrolledFactor,
} from '../../auth/second-factor';
import { TotpEnrolment } from './TotpEnrolment';
import './second-factor.css';

export interface SecondFactorSheetProps {
  onClose: () => void;
  /** What gets the keyboard back when the sheet closes: the profile's own button. */
  returnFocusTo?: RefObject<HTMLElement | null>;
}

/**
 * The person's two-step sign-in, in the shared drawer: what is set up, a way to add
 * an authenticator app, and a way to remove one. Enrolment lives with the identity, so the sheet
 * talks to the sign-in service, not the API.
 */
export function SecondFactorSheet({ onClose, returnFocusTo }: SecondFactorSheetProps): ReactNode {
  const state = useFactors();
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      variant="drawer"
      size="sm"
      title="Two-step sign-in"
      {...(returnFocusTo ? { returnFocusTo } : {})}
    >
      <SheetBody {...state} />
    </Dialog>
  );
}

type Factors = ReturnType<typeof useFactors>;

/** What the sheet says when two-step sign-in cannot be changed from this session. */
export const SIGN_IN_AGAIN = 'Sign out and sign in again to change two-step sign-in.';

function SheetBody(state: Factors): ReactNode {
  const { available, adding, setAdding, refresh } = state;
  if (available === null) return <p className="sheet__line">Checking your sign-in…</p>;
  if (!available) return <p className="sheet__line">{state.error ?? SIGN_IN_AGAIN}</p>;
  if (!adding) return <Status {...state} />;
  return (
    <>
      <p className="sheet__line">
        Once it is on, signing in asks for a code from your app as well as your password.
      </p>
      <TotpEnrolment
        onDone={() => {
          setAdding(false);
          void refresh();
        }}
        onCancel={() => setAdding(false)}
      />
    </>
  );
}

/** On or off, the steps set up, and the way to add one. */
function Status({ factors, error, setAdding, remove }: Factors): ReactNode {
  return (
    <>
      <p className="sheet__line">
        {factors.length === 0
          ? 'Off. Your password or Google sign-in is all it takes.'
          : 'On. Signing in also asks for a code from your app.'}
      </p>
      {factors.map((f) => (
        <FactorRow key={f.uid} factor={f} onRemove={() => void remove(f.uid)} />
      ))}
      {error && (
        <p className="sheet__line sheet__line--error" role="alert">
          {error}
        </p>
      )}
      {factors.length === 0 && (
        <Button type="button" variant="primary" onClick={() => setAdding(true)}>
          Set up an authenticator app
        </Button>
      )}
    </>
  );
}

/** One step set up. Removing it asks first: the bin opens the question, Remove answers it. */
function FactorRow({ factor, onRemove }: { factor: EnrolledFactor; onRemove: () => void }) {
  const [asking, setAsking] = useState(false);
  const since = factor.since ? new Date(factor.since).toLocaleDateString() : null;
  return (
    <>
      <div className="factor">
        <Smartphone size={18} aria-hidden="true" />
        <span className="factor__who">
          <span className="factor__name">{factor.name}</span>
          {since && <span className="factor__hint">Since {since}</span>}
        </span>
        <button
          type="button"
          className="factor__remove"
          aria-label={`Remove ${factor.name}`}
          aria-expanded={asking}
          onClick={() => setAsking((a) => !a)}
        >
          <Trash2 size={15} aria-hidden="true" />
        </button>
      </div>
      {asking && (
        <RemoveAsk name={factor.name} onRemove={onRemove} onKeep={() => setAsking(false)} />
      )}
    </>
  );
}

interface RemoveAskProps {
  name: string;
  onRemove: () => void;
  onKeep: () => void;
}

function RemoveAsk({ name, onRemove, onKeep }: RemoveAskProps): ReactNode {
  return (
    <div role="group" aria-label={`Remove ${name}?`}>
      <p className="sheet__line">Signing in stops asking for a code.</p>
      <div className="totp__actions">
        <Button type="button" variant="ghost" onClick={onKeep}>
          Keep
        </Button>
        <Button type="button" variant="danger" onClick={onRemove}>
          Remove
        </Button>
      </div>
    </div>
  );
}

function useFactors() {
  const [available, setAvailable] = useState<boolean | null>(null);
  const [factors, setFactors] = useState<EnrolledFactor[]>([]);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refresh = useCallback(
    () =>
      load()
        .then(({ ok, list }) => {
          setAvailable(ok);
          setFactors(list);
        })
        .catch((e: unknown) => {
          // Nothing was read: the sheet says why instead of checking for ever.
          setAvailable((known) => known ?? false);
          setError(secondStepMessage(e));
        }),
    [],
  );
  useEffect(() => void refresh(), [refresh]);
  const remove = async (uid: string): Promise<void> => {
    setError(null);
    try {
      await removeFactor(uid);
      await refresh();
    } catch (e) {
      setError(secondStepMessage(e));
    }
  };
  return { available, factors, adding, error, setAdding, refresh, remove };
}

/** Whether the flows can run here, and the steps set up when they can. */
async function load(): Promise<{ ok: boolean; list: EnrolledFactor[] }> {
  const ok = await secondFactorAvailable();
  return { ok, list: ok ? await enrolledFactors() : [] };
}
